import type { Reply } from 'models';
import { type RefObject, useEffect, useState } from 'react';
import { type InputRef, type RichTextEditorBase, type RichTextMentions, Section } from 'ui';
import { handleAxiosError } from 'ui';
import { CommentForm, type CommentFormValues } from '../comment-form';
import { useGetCommentParentId } from '../hooks';
import { useCreateCommentMutation } from '../queries';
import { CreateCommentFormPlaceholder } from './create-comment-form-placeholder';

export type CommentMode = 'input' | 'placeholder';

type P = {
  comment?: Reply;
  commentFormContainerRef?: RefObject<HTMLDivElement | null>;
  contentObjectId: string;
  editorRef?: RefObject<null | RichTextEditorBase>;
  initialMode?: CommentMode;
  inputRef?: RefObject<InputRef | null>;
  onRequestMentions: RichTextMentions;
  onSuccess?: () => void;
  scrollOnMount?: boolean;
};

const CreateCommentForm = ({
  comment,
  commentFormContainerRef,
  contentObjectId,
  editorRef,
  initialMode = 'placeholder',
  inputRef,
  onRequestMentions,
  onSuccess,
  scrollOnMount,
}: P) => {
  const { isPending: isCreatingComment, mutate: createComment } = useCreateCommentMutation();
  const [mode, setMode] = useState<CommentMode>(initialMode);
  const parentId = useGetCommentParentId({ comment, contentObjectId });

  const onFinish = (values: CommentFormValues) => {
    createComment(
      {
        attachments: values.attachments.files,
        contentObjectId,
        htmlContent: values.htmlContent,
        parentId,
        textContent: values.textContent,
      },
      {
        onError(error) {
          handleAxiosError(error, "Something went wrong. Comment couldn't be added.");
        },
        onSuccess() {
          onSuccess?.();
          setMode('placeholder');
        },
      },
    );
  };

  // TODO: This is hack for scroll to comment form after data are fetched
  // will be removed when modal become scrollable again
  useEffect(() => {
    if (!scrollOnMount || !commentFormContainerRef) {
      return;
    }

    const timeOut = setTimeout(() => {
      commentFormContainerRef.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }, 200);

    return () => {
      clearTimeout(timeOut);
    };
  }, [scrollOnMount, commentFormContainerRef]);

  return (
    <div ref={commentFormContainerRef}>
      {mode === 'placeholder' ? (
        <Section lineBottom={false} paddingBottom={false}>
          <CreateCommentFormPlaceholder
            inputRef={inputRef}
            onRequestInput={() => {
              setMode('input');
            }}
          />
        </Section>
      ) : null}
      {mode === 'input' ? (
        <CommentForm
          editorRef={editorRef}
          isLoading={isCreatingComment}
          onRequestMentions={onRequestMentions}
          onSubmit={onFinish}
        />
      ) : null}
    </div>
  );
};

export default CreateCommentForm;
